import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { Plus, ArrowLeft, LayoutGrid, Star, MoreVertical, Trash2, Users } from 'lucide-react'
import { toast } from 'sonner'
import { projectsApi, boardsApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Board, CreateBoardRequest, MemberRole, Visibility } from '@/types'
import MemberManagement from '@/components/project/MemberManagement'
import VisibilitySelector, { VisibilityBadge } from '@/components/project/VisibilitySelector'

// Gradient backgrounds for boards (Trello-style)
const BOARD_BACKGROUNDS = [
  // Gradient backgrounds - Row 1
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple Haze
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink Sunset
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Ocean Blue
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Fresh Mint
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Warm Flame
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', // Soft Lavender
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // Rose Petal
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // Peach
  // Row 2 - More gradients
  'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', // Sky Blue
  'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)', // Dusty Rose
  'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)', // Cool Sky
  'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)', // Sunny Day
  'linear-gradient(135deg, #9890e3 0%, #b1f4cf 100%)', // Spring
  'linear-gradient(135deg, #ebc0fd 0%, #d9ded8 100%)', // Soft Pink
  'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)', // Lime
  'linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)', // Violet
  // Row 3 - Solid colors (darker tones for contrast)
  '#1e3a5f', // Navy
  '#2d3748', // Charcoal
  '#553c9a', // Purple
  '#234e52', // Teal
  '#744210', // Brown
  '#702459', // Magenta
  '#1a365d', // Dark Blue
  '#22543d', // Forest
]

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [boardToDelete, setBoardToDelete] = useState<Board | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [formData, setFormData] = useState<CreateBoardRequest>({
    board_name: '',
    board_description: '',
    board_type: 'KANBAN',
    background_color: BOARD_BACKGROUNDS[0],
  })

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getById(projectId!),
    enabled: !!projectId,
  })

  const { data: boards = [], isLoading: boardsLoading } = useQuery({
    queryKey: ['project-boards', projectId],
    queryFn: () => projectsApi.getBoards(projectId!),
    enabled: !!projectId,
  })

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => projectsApi.getMembers(projectId!),
    enabled: !!projectId,
  })

  const updateProjectMutation = useMutation({
    mutationFn: (data: { visibility: Visibility }) => projectsApi.update(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast.success('Project updated')
    },
    onError: (error: any) => {
      const err = error.response?.data?.message
      const errorMessage = typeof err === 'string' ? err : error.message || 'Failed to update project'
      toast.error(errorMessage)
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateBoardRequest) => projectsApi.createBoard(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-boards', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast.success('Board created successfully')
      setCreateDialogOpen(false)
      resetForm()
    },
    onError: (error: any) => {
      const err = error.response?.data?.message
      const errorMessage = typeof err === 'string' ? err : error.message || 'Failed to create board'
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (boardId: string) => boardsApi.delete(boardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-boards', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast.success('Board deleted successfully')
      setDeleteDialogOpen(false)
      setBoardToDelete(null)
    },
    onError: (error: any) => {
      const err = error.response?.data?.message
      const errorMessage = typeof err === 'string' ? err : error.message || 'Failed to delete board'
      toast.error(errorMessage)
    },
  })

  const toggleStarMutation = useMutation({
    mutationFn: ({ id, isStarred }: { id: string; isStarred: boolean }) =>
      boardsApi.update(id, { is_starred: isStarred }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-boards', projectId] })
    },
  })

  const resetForm = () => {
    setFormData({
      board_name: '',
      board_description: '',
      board_type: 'KANBAN',
      background_color: BOARD_BACKGROUNDS[0],
    })
  }

  const handleDeleteClick = (board: Board) => {
    setBoardToDelete(board)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (boardToDelete) {
      deleteMutation.mutate(boardToDelete.board_id)
    }
  }

  const handleCreate = () => {
    if (!formData.board_name.trim()) {
      toast.error('Board name is required')
      return
    }
    createMutation.mutate(formData)
  }

  const handleToggleStar = (board: Board) => {
    toggleStarMutation.mutate({ id: board.board_id, isStarred: !board.is_starred })
  }

  const handleAddMember = async (userId: string, role: MemberRole) => {
    await projectsApi.addMember(projectId!, { user_id: userId, member_role: role })
    queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
  }

  const handleUpdateMember = async (userId: string, role: MemberRole) => {
    await projectsApi.updateMember(projectId!, userId, { member_role: role })
    queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
  }

  const handleRemoveMember = async (userId: string) => {
    await projectsApi.removeMember(projectId!, userId)
    queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
  }

  const handleVisibilityChange = (visibility: Visibility) => {
    updateProjectMutation.mutate({ visibility })
  }

  const isLoading = projectLoading || boardsLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading project...</div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">Project not found.</div>
      </div>
    )
  }

  const starredBoards = boards.filter(b => b.is_starred)
  const otherBoards = boards.filter(b => !b.is_starred)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/projects">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.project_color }} />
            <h1 className="text-3xl font-bold">{project.project_name}</h1>
            <VisibilityBadge visibility={project.visibility} />
          </div>
          {project.project_description && (
            <p className="text-muted-foreground mt-1">{project.project_description}</p>
          )}
        </div>
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          <Users className="mr-2 h-4 w-4" />
          Members ({members.length})
        </Button>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Board
        </Button>
      </div>

      {boards.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <LayoutGrid className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No boards yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first board to start organizing tasks with lists and cards.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Board
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {starredBoards.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                Starred Boards
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {starredBoards.map((board) => (
                  <BoardCard
                    key={board.board_id}
                    board={board}
                    onDelete={handleDeleteClick}
                    onToggleStar={handleToggleStar}
                  />
                ))}
              </div>
            </div>
          )}

          {otherBoards.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">All Boards</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {otherBoards.map((board) => (
                  <BoardCard
                    key={board.board_id}
                    board={board}
                    onDelete={handleDeleteClick}
                    onToggleStar={handleToggleStar}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Board Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Board</DialogTitle>
            <DialogDescription>
              Add a new board to organize your tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="board_name">Board Name</Label>
              <Input
                id="board_name"
                value={formData.board_name}
                onChange={(e) => setFormData({ ...formData, board_name: e.target.value })}
                placeholder="Enter board name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="board_description">Description (optional)</Label>
              <Textarea
                id="board_description"
                value={formData.board_description || ''}
                onChange={(e) => setFormData({ ...formData, board_description: e.target.value })}
                placeholder="Enter board description"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Background</Label>
              <div className="grid grid-cols-8 gap-2">
                {BOARD_BACKGROUNDS.map((bg) => (
                  <button
                    key={bg}
                    type="button"
                    className={`w-8 h-8 rounded-md border-2 transition-all ${
                      formData.background_color === bg
                        ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-foreground/50'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ background: bg }}
                    onClick={() => setFormData({ ...formData, background_color: bg })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm() }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Board'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Board</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{boardToDelete?.board_name}"? This will also delete all lists and cards within this board. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Settings Sheet */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Project Settings</SheetTitle>
            <SheetDescription>
              Manage project visibility and members
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <Tabs defaultValue="members">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="members">Members</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="members" className="mt-4">
                <MemberManagement
                  resourceId={projectId!}
                  resourceType="project"
                  members={members}
                  isLoading={membersLoading}
                  onAddMember={handleAddMember}
                  onUpdateMember={handleUpdateMember}
                  onRemoveMember={handleRemoveMember}
                  currentUserId={user?.user_id}
                />
              </TabsContent>
              <TabsContent value="settings" className="mt-4 space-y-6">
                <VisibilitySelector
                  value={project.visibility}
                  onChange={handleVisibilityChange}
                  disabled={updateProjectMutation.isPending}
                />
                <p className="text-sm text-muted-foreground">
                  {project.visibility === 'PUBLIC'
                    ? 'All team members in your organization can view and access this project.'
                    : 'Only members you add to this project can view and access it.'}
                </p>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

interface BoardCardProps {
  board: Board
  onDelete: (board: Board) => void
  onToggleStar: (board: Board) => void
}

function BoardCard({ board, onDelete, onToggleStar }: BoardCardProps) {
  // Check if background is a gradient or solid color
  const isGradient = board.background_color?.startsWith('linear-gradient')
  const backgroundStyle = isGradient
    ? { background: board.background_color }
    : { backgroundColor: board.background_color }

  return (
    <Link to={`/boards/${board.board_id}`}>
      <Card
        className="hover:shadow-lg transition-all cursor-pointer group h-full border-0 overflow-hidden"
        style={backgroundStyle}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-white" />
              <CardTitle className="text-lg text-white">{board.board_name}</CardTitle>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleStar(board) }}
                className="p-1 hover:bg-white/20 rounded"
              >
                <Star className={`h-4 w-4 ${board.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-white/70'}`} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                  <button className="p-1 hover:bg-white/20 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreVertical className="h-4 w-4 text-white" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => { e.stopPropagation(); onDelete(board) }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {board.board_description && (
            <CardDescription className="line-clamp-2 text-white/70">{board.board_description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-white/70">
            {board.list_count} {board.list_count === 1 ? 'list' : 'lists'} · {board.card_count} {board.card_count === 1 ? 'card' : 'cards'}
          </p>
        </CardContent>
      </Card>
    </Link>
  )
}

